'use client';

import { usePathname } from 'next/navigation';

import {
  TOP_NAV_DATA_GA,
  TOP_NAV_DATA_TEST,
} from '@/app/components/layouts/top-navigation/tracking';
import type { MenuLinkType } from '@/app/types/top-navigation';
import { createDataGA } from '@/app/utils/createDataGA';
import type { BaseDisplayConfigs } from '@/components/layouts/top-navigation/shared/menu-item/base';
import { Icon } from '@/components/shared/element/icons';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { type CSS, css } from '@/stitches.config';
import type { IIcon } from '@/types/shared/icons';

export const MenuItemCss = css({
  transition: 'color 0.3s var(--transition-easing)',
  display: 'flex',
  position: 'relative',
  cursor: 'pointer',
  alignItems: 'center',
  py: '$space-1-5',
  textAlign: 'left',
  color: '#1d1d1d', // tailwind gray-900 (base color)
  '@hover': {
    '&:hover': {
      color: '$primary',
    },
  },
  variants: {
    noContainer: {
      true: {},
      false: {
        px: '$space-4',
        '@md': {
          px: '$space-10',
        },
        '@xl': {
          px: '$space-0',
        },
      },
    },
    isOpenNewTab: {
      true: {
        gap: '$space-2',
      },
      false: {},
    },
    hasIcon: {
      true: {
        gap: '$space-2',
        justifyContent: 'space-between',
      },
      false: {},
    },
  },
  defaultVariants: {
    isOpenNewTab: false,
    noContainer: false,
    hasIcon: false,
  },
});

interface ItemProps extends BaseDisplayConfigs {
  item: MenuLinkType;
  icon?: IIcon;
  css?: CSS;
  noContainer?: boolean;
  onClickNext?: () => void;
  onClose?: () => void;
}

export const Item = ({
  item,
  icon,
  noContainer = false,
  css,
  onClickNext,
  onClose,
}: ItemProps) => {
  const pathname = usePathname();
  const styleConfigs = {
    hasIcon: !!icon,
    noContainer,
    css,
  };

  if (typeof onClickNext === 'function' || pathname === item.href) {
    return (
      <Box
        className={MenuItemCss(styleConfigs)}
        aria-label={item.label}
        role="menuitem"
        test={{
          dataTest: TOP_NAV_DATA_TEST.navigationMenuLink,
        }}
        tracking={{
          dataGA: createDataGA({
            eventName: TOP_NAV_DATA_GA.topNavigation,
            text: item.label,
            click: true,
          }),
        }}
        onClick={pathname === item.href ? onClose : onClickNext}
      >
        <Text font="bold" size="text-body-1" variant="text-current">
          {item.label}
        </Text>
        {!!icon && <Icon icon={icon} color="icon-current" />}
      </Box>
    );
  }

  return (
    <Link
      href={item.href}
      target={item.target}
      aria-label={item.label}
      role="menuitem"
      test={{
        dataTest: TOP_NAV_DATA_TEST.navigationMenuLink,
      }}
      tracking={{
        dataGA: createDataGA({
          eventName: TOP_NAV_DATA_GA.topNavigation,
          text: item.label,
          url: item.href,
          click: true,
        }),
      }}
      className={MenuItemCss({
        ...styleConfigs,
        isOpenNewTab: item.target === '_blank',
      })}
    >
      {item.labelHtml ? (
        <Text
          font="bold"
          size="text-body-1"
          variant="text-current"
          wrap
          dangerouslySetInnerHTML={{ __html: item.labelHtml }}
        />
      ) : (
        <Text font="bold" size="text-body-1" variant="text-current">
          {item.label}
        </Text>
      )}

      {item.target === '_blank' && (
        <Icon icon="open-in-new" color="icon-current" />
      )}
      {item.target !== '_blank' && !!icon && (
        <Icon icon={icon} color="icon-current" />
      )}
    </Link>
  );
};
